/**
 * VE-25873 Unit 3 — resolve a file's parent request at editor entry and warm
 * the folder view so exiting lands on that request.
 *
 * `resolveEditorReturnTarget` runs at the top of each editor's route-init
 * saga (media and document). It resolves the TDO's owning case/folder — the
 * URL's case when present and still a parent of the TDO, else the first
 * non-Trash parent — and records it as `editorReturnTarget` in casemedia
 * state. A case-less direct URL (/files/:tdoId) is additionally rewritten to
 * /case/:caseId/files/:tdoId via history REPLACE, which enables the in-editor
 * case-media switcher; a URL that already carries a case is never rewritten.
 *
 * VE-26943: exiting the editor does NOT land on Unassigned Media — the previous
 * selection is kept, as it always has been. An unassigned file has no folder at
 * all, so the owning-case lookup returns nothing for it, indistinguishably from a
 * failed lookup, a missing TDO, or Trash-only parents. Telling them apart needs
 * the redact-state SDO, which this layer does not consult.
 *
 * VE-30022: `resolveEditorReturnTarget` is also where the folder view's trip
 * away starts (`actionMarkFolderViewLeft`). It is the one hook both editors
 * already share at route init, which is exactly the moment the folder view was
 * left — and the return's reuse window is measured from it rather than from
 * whenever the file list happened to load. Arriving from ANOTHER editor route
 * does not restart it: this saga runs on in-editor file switches and redirects
 * too, and those never pass through the folder view.
 *
 * `warmEditorReturnTargetTree` is forked INSIDE the editor's route saga, so
 * it lives exactly as long as the session (redux-first-router cancels the
 * route saga on exit — no always-on registration, no exit observation). When
 * the target isn't already the folder view's selection it forks
 * warmCaseTreeWatchers (the casemedia watchers that serve these fetches are
 * otherwise only alive on the Home route) and loads root folders, the
 * target's column chain, and each column's contents into state while the
 * user is still editing.
 *
 * The exit itself needs no code here: CaseFolders consumes
 * `editorReturnTarget` once on mount as the TOP branch of its
 * selection-priority effect — warmed target → render from state (no
 * loading); not warmed → deep-link fallback; pending explicit deep-link
 * (a document exit that is NOT holding its request) → just clear the flag.
 *
 * VE-30022: that last case used to cover every document exit, because the
 * editor deep-linked unconditionally and its navToId short-circuited the two
 * branches above — so the decision this stage exists to make was never made,
 * and a direct URL fetched the chain twice (warm-up, then exit). A document
 * exit that IS holding its request now routes home with no payload and takes
 * the warmed-target branch, like the media editor.
 */
import {
  ROUTE_DOCUMENT_DETAILS,
  ROUTE_MEDIA_DETAILS,
  selectCurrentRoutePayload,
  selectPreviousRoute,
} from '@common-modules/routing';
import { FolderId, TDOId } from '@common-modules/universal/models/Brands';
import {
  REQUEST_USER_ORGANIZATION_FAILURE,
  REQUEST_USER_ORGANIZATION_SUCCESS,
} from '@common/user-onboarding/actions/organization';
import { OnboardingSlice } from '@common/user-onboarding/models';
import { selectEnableFolderView } from '@common/user-onboarding/selectors/organization';
import { selectStore } from '@common/user-onboarding/selectors/store';
import { checkIsCaseId } from '@utils';
import { redirect } from 'redux-first-router';
import {
  delay,
  fork,
  put,
  putResolve,
  race,
  select,
  take,
} from 'typed-redux-saga/macro';
import {
  actionFetchFolderRequest,
  actionMarkFolderViewLeft,
  actionSetEditorReturnTarget,
  actionfetchSelectedCaseOrFolderTree,
  FETCH_ROOT_FOLDERS_FAILURE,
  FETCH_ROOT_FOLDERS_SUCCESS,
} from '../actions';
import {
  selectDefaultLimit,
  selectDefaultOffset,
  selectEditorReturnTarget,
  selectRootFolderId,
  selectSelectedFolders,
} from '../selectors';
import { fetchTdoOwningCase } from '../services';
import { isUnassignedFolderId } from '../unassigned';
import { CaseMediaState, FolderStateType } from '../store';
import { warmCaseTreeWatchers } from './index';

type EditorRouteAction = (payload: {
  tdoId: TDOId;
  caseId?: string;
}) => Parameters<typeof redirect>[0];

// Safety nets so a hung org fetch or root-folders fetch can never stall the
// editor (resolution) or leave the warm-up waiting forever.
const ORG_LOAD_TIMEOUT_MS = 15_000;
const ROOT_FOLDERS_TIMEOUT_MS = 15_000;

// The org config that selectEnableFolderView reads is populated by
// REQUEST_USER_ORGANIZATION_SUCCESS; the default store org has an empty id.
const selectIsOrgLoaded = (state: OnboardingSlice): boolean =>
  Boolean(selectStore(state)?.organization?.id);

// "Selected" means a RENDERABLE chain entry: CaseFolders keys its columns on
// folderPath, so an id-matching minimal placeholder (no folderPath) does not
// count — the tree still needs fetching for the exit to land visibly.
const isTargetSelected = (
  selectedFolders: FolderStateType[],
  folderId: string
): boolean =>
  selectedFolders.some(
    (f) =>
      (f.id === folderId ||
        (f.treeObjectId as string | undefined) === folderId) &&
      (f.folderPath?.length ?? 0) > 0
  );

/**
 * Folder-view gate with org readiness. On a cold direct link the org config
 * that selectEnableFolderView reads may not have loaded yet, and the flag
 * would read a stale `false` — wait once for the org to load, but only when
 * it isn't loaded, so a genuinely folder-view-off org returns immediately.
 * Raced with failure/timeout so the editor never stalls on a hung org fetch.
 */
function* waitForFolderViewEnabled() {
  const orgLoaded = yield* select(selectIsOrgLoaded);
  if (!orgLoaded) {
    yield* race({
      ok: take(REQUEST_USER_ORGANIZATION_SUCCESS),
      failed: take(REQUEST_USER_ORGANIZATION_FAILURE),
      timeout: delay(ORG_LOAD_TIMEOUT_MS),
    });
  }
  return yield* select(selectEnableFolderView);
}

/**
 * Resolves the opened file's parent request, records it as the session's
 * return target, and injects the case into a case-less URL. Returns true when
 * it redirected — the caller must stop; the route re-runs with the case
 * present and short-circuits on the already-recorded target (no loop).
 * Call it with the calling editor's OWN route action.
 */
export function* resolveEditorReturnTarget(routeAction: EditorRouteAction) {
  const payload = yield* select(selectCurrentRoutePayload);
  const { caseId, tdoId } = (payload ?? {}) as {
    caseId?: string;
    tdoId?: TDOId;
  };
  if (!tdoId) {
    return false;
  }
  /**
   * VE-30022: the folder view has just been left for an editor — start the
   * trip that decides whether the way back may reuse its columns. Stamped
   * before anything that can return early or await, so a fast exit is still
   * measured from the right moment; the reducer takes the column snapshot. On
   * a cold direct link there is no folder view behind us, so the snapshot is
   * empty and nothing qualifies — which is what makes deep links still fetch.
   *
   * NOT on an editor-to-editor hop, which is the difference between measuring
   * the trip and measuring the last route change. This saga runs on every
   * entry to an editor route, and plenty of those never touch the folder view:
   * the case-media drawer switching files (`fileRouteAction`), the document
   * editor's own switcher, the trim redirect to a new TDO, "view redacted
   * file", and the case-less URL's own redirect back into this route. Stamping
   * on those restarts the clock while the user is still away, so a reviewer
   * working through a request's files for an hour would exit on a 30-second
   * trip and reuse an hour-old list — the unbounded staleness this window
   * exists to stop. Arriving from an editor, the trip already running is the
   * one that means "the folder view was left", so it is left alone.
   */
  const previousRoute = yield* select(selectPreviousRoute);
  const cameFromEditor =
    previousRoute?.type === ROUTE_MEDIA_DETAILS.type ||
    previousRoute?.type === ROUTE_DOCUMENT_DETAILS.type;
  if (!cameFromEditor) {
    yield* put(actionMarkFolderViewLeft());
  }
  if (caseId) {
    // Explicit case in the URL (FR-3.2: never rewrite). Record it as the
    // return target: the URL's case when the file still lives under it, else
    // the file's actual parent (stale link, FR-3.1).
    const recorded = yield* select(selectEditorReturnTarget);
    if (recorded?.folderId === caseId) {
      return false;
    }
    const selectedFolders = yield* select(selectSelectedFolders);
    const selected = selectedFolders.find(
      (f) =>
        f.id === caseId || (f.treeObjectId as string | undefined) === caseId
    );
    // Record SYNCHRONOUSLY — nothing above this point awaits, so even an
    // exit within the very first second finds a target and lands on the
    // file's request (loading) instead of the previously-selected folder.
    // isCase comes from state when the case is already the selection;
    // otherwise it is provisionally `true` (folder-view files live in
    // requests in practice) and the lookup below refines it — the refinement
    // is cancelled with this saga on exit, so the provisional value is what
    // an early exit consumes.
    yield* put(
      actionSetEditorReturnTarget({
        folderId: caseId as FolderId,
        isCase: selected ? checkIsCaseId(selected) : true,
      })
    );
    if (selected) {
      // Normal folder-view open: the exit will be a no-op — no network
      // needed at all (FR-3.6 zero-cost requirement).
      return false;
    }
    // Refine in the BACKGROUND: a cold /case/X/files/Y open must not delay
    // the editor load (the caller forks loadMediaDetailsPage only after this
    // returns) on the org gate + a GQL round-trip. The provisional target is
    // already recorded above; this attached fork is cancelled with the route
    // saga on exit, exactly as the comment above describes.
    yield* fork(refineEditorReturnTarget, tdoId, caseId as FolderId);
    return false;
  }
  // Case-less direct link: discover the owning case and inject it.
  const folderViewEnabled = yield* waitForFolderViewEnabled();
  if (!folderViewEnabled) {
    return false;
  }
  const owning = (yield* putResolve(fetchTdoOwningCase({ tdoId }) as never)) as
    | { caseId: FolderId; isCase: boolean }
    | null
    | undefined;
  if (!owning) {
    // No accessible owning case — the editor opens exactly as today (FR-3.5).
    //
    // VE-26943 briefly recorded Unassigned Media here, on the premise that "no
    // owning case" meant "unassigned". That was wrong twice over: an unassigned
    // file DOES resolve an owner (the org root, a non-case folder), so it never
    // reaches this branch; and what does reach it is a lookup failure, a missing
    // TDO, or a file whose only parents are in Trash — none of them unassigned.
    // The real test is below.
    return false;
  }
  // VE-26943 note: exiting the editor does NOT land back on Unassigned Media —
  // it keeps the previous selection, as it always has. Sending it there needs a
  // signal this layer doesn't have: an unassigned file has no folder at all, so
  // fetchTdoOwningCase returns null for it — indistinguishably from a lookup
  // failure, a missing TDO, or a file whose only parents are in Trash. The
  // authoritative test is whether the file has a redact-state SDO. (An earlier
  // attempt keyed on the owning folder being the org root; that never fires,
  // because there is no owning folder to inspect.)
  yield* put(
    actionSetEditorReturnTarget({
      folderId: owning.caseId,
      isCase: owning.isCase,
    })
  );
  // History REPLACE — no extra Back entry (FR-3.2).
  yield* put(redirect(routeAction({ tdoId, caseId: owning.caseId })));
  return true;
}

/**
 * Background refinement of the return target for a URL whose case is not the
 * current folder-view selection (cold open): once the org/folder-view gates
 * settle, look up the TDO's owning case and correct the provisional target.
 * Runs as an attached fork of the editor's route saga, so an exit before it
 * completes cancels it and the provisional value is what the exit consumes.
 */
function* refineEditorReturnTarget(tdoId: TDOId, caseId: FolderId) {
  // The refinement lookup respects the org/folder-view gates.
  const folderViewEnabled = yield* waitForFolderViewEnabled();
  if (!folderViewEnabled) {
    return;
  }
  // putResolve returns the thunk's resolved value at runtime (redux-thunk);
  // typed-redux-saga only types putResolve for plain actions, hence the
  // cast.
  const owning = (yield* putResolve(
    fetchTdoOwningCase({ tdoId, preferFolderId: caseId }) as never
  )) as { caseId: FolderId; isCase: boolean } | null | undefined;
  if (owning) {
    yield* put(
      actionSetEditorReturnTarget({
        folderId: owning.caseId,
        isCase: owning.isCase,
      })
    );
  }
}

/**
 * Warms the folder-view column tree for the session's return target while the
 * user is still in the editor, so the exit renders it from state with no
 * loading (FR-3.6). Fork this INSIDE the editor's route saga: it is then
 * cancelled with the route on exit, and in-flight GQL thunks still complete —
 * their success actions are served by the Home route's own caseMediaSaga.
 *
 * Root folders must resolve BEFORE the chain fetch, or
 * fetchCaseOrFolderTreeSuccess can't strip the root from the folderPath and
 * the root renders as a duplicate column (C6). Bails silently on any failure —
 * CaseFolders then falls back to a deep-link (loading, but still correct).
 */
export function* warmEditorReturnTargetTree() {
  const target: CaseMediaState['editorReturnTarget'] = yield* select(
    selectEditorReturnTarget
  );
  if (!target) {
    return;
  }
  // VE-26943: nothing to warm for Unassigned Media — it has no folder chain, and
  // its files are loaded by selecting it. Fetching a tree for the sentinel would
  // send it to the folder API.
  if (isUnassignedFolderId(target.folderId)) {
    return;
  }
  const selectedFolders = yield* select(selectSelectedFolders);
  if (isTargetSelected(selectedFolders, target.folderId)) {
    // Already the current selection (normal folder-view open) — the columns
    // are warm by definition; fetching again would only reload them.
    return;
  }
  yield* fork(warmCaseTreeWatchers);
  let rootFolderId = yield* select(selectRootFolderId);
  if (!rootFolderId) {
    const limit = yield* select(selectDefaultLimit);
    const offset = yield* select(selectDefaultOffset);
    yield* put(actionFetchFolderRequest({ limit, offset }));
    yield* race({
      ok: take(FETCH_ROOT_FOLDERS_SUCCESS),
      failed: take(FETCH_ROOT_FOLDERS_FAILURE),
      timeout: delay(ROOT_FOLDERS_TIMEOUT_MS),
    });
    rootFolderId = yield* select(selectRootFolderId);
    if (!rootFolderId) {
      return;
    }
  }
  yield* put(
    actionfetchSelectedCaseOrFolderTree({
      folderId: target.folderId,
      isCase: target.isCase,
    })
  );
  // Returning here does NOT end the task: the attached warmCaseTreeWatchers
  // fork keeps serving the chain/contents cascade until the route saga is
  // cancelled at exit.
}
